import { useState } from "react";
import useTranslator from "../../hooks/useTranslator";
import ProfileCompletionPercentage from "./ProfileCompletionPercentage";
import { Tooltip as ReactTooltip } from "react-tooltip";
import {
  getGuarantorStatus,
  ONFIDO_TOOLTIP_PRIORITY,
} from "../../helper/common";
import { resolveFinancialDisplayError } from "../../Utils/bankStatementClassification";
import { ChainLinkIcon, PencilEditIcon } from "../../helper/svgHelper";
import SkorProtectBadge from "../SkorProtectBadge/SkorProtectBadge";
import styles from "./TenantHeaderCard.module.css";

const TenantHeaderCard = ({
  percent,
  percentageLoading = false,
  missingDocs = [],
  kycStatus = "Failed",
  kycState = "failed",
  bankConnected = false,
  financial_info_errors = null,
  bankStatementClassification = null,
  rentalStatus = null,
  guarantorCount = 0,
  guarantorCompleteCount = null,
  guarantorPendingCount = null,
  gurantorsIncome = [],
  flatmateCount = 0,
  pendingActionCount = 0,
  onPendingActionClick,
  onRedoKyc,
  onConnectBank,
  onAddGuarantor,
  onAddFlatmate,
  onSetTargetRent,
  onEditLandlordNote,
  onOpenGuarantor = null,
  disabled = false,
  processing = false,
  readOnly = false,
  user = null,
  llShowOnly = false,
  isGuarantor = false,
}) => {
  const { _t } = useTranslator();
  const [expanded, setExpanded] = useState(false);
  const visibleDocs = expanded ? missingDocs : missingDocs.slice(0, 3);
  const normalizedRentalStatus =
    rentalStatus === null || rentalStatus === undefined
      ? null
      : String(rentalStatus);
  const isKycFailed =
    kycState === _t("declined") ||
    kycState === "declined" ||
    kycState === _t("failed") ||
    kycState === "failed" ||
    kycState === "review";
  const isKycOk = !isKycFailed;
  const isRentalElevated = normalizedRentalStatus?.toLowerCase() === "elevated";
  const hasRentalStatus =
    normalizedRentalStatus !== null &&
    normalizedRentalStatus !== undefined &&
    normalizedRentalStatus.trim() !== "";
  const kycActionClass = isKycOk
    ? `${styles.action} ${styles.actionSecondary}`
    : styles.action;
  const weakActionClass = `${styles.action} ${styles.actionSecondary}`;
  const disabledStyle = disabled
    ? { opacity: 0.5, cursor: "not-allowed" }
    : undefined;
  const disabledTooltip = readOnly
    ? _t("viewOnly", "View only — you can't make changes to this profile.")
    : processing
      ? _t(
          "profileProcessingWait",
          "Your profile is still being processed. Please wait until it's ready."
        )
      : disabled
        ? _t(
            "profileOutdatedRenewFirst",
            "Your profile is outdated. Please renew your profile first."
          )
        : undefined;

  const renderTooltip = (id) =>
    disabled ? (
      <ReactTooltip
        id={id}
        place="top"
        className="react-tooltip-styling"
        render={() => <p>{disabledTooltip}</p>}
      />
    ) : null;

  const getConsiderResult = (breakdown, path) => {
    const pathParts = path.split(".");
    const section = breakdown?.[pathParts.shift()];
    const field = pathParts.reduce((value, part) => {
      if (!value) return undefined;
      return (
        value.breakdown?.[part] ??
        (part === "properties" ? value.properties : value.properties?.[part]) ??
        value[part]
      );
    }, section);

    if (!field) return false;
    if (field === "consider" || field.result === "consider") return true;

    return Object.values(field.properties ?? {}).some(
      (value) => value === "consider"
    );
  };

  const getReviewReason = () => {
    const output =
      user?.onfido_user_kyc?.onfido_workflow_res?.resource?.output ??
      user?.onfido_user_kyc?.onfido_workflow_res?.output;
    const status =
      user?.onfido_user_kyc?.onfido_workflow_res?.resource?.status ??
      user?.onfido_user_kyc?.onfido_workflow_res?.status;

    if (status === "review" && output?.document_type === "unidentified") {
      return llShowOnly
        ? _t(
            "onfidoUnidentifiedDocumentReviewLandlordMessage",
            "The tenant uploaded unsupported identification documents, so identity verification could not be completed successfully. Please ask the tenant to upload a valid government-issued ID with a visible photo. Copies or scanned documents are not accepted."
          )
        : _t(
            "onfidoUnidentifiedDocumentReviewTenantMessage",
            "Please upload a photo of a valid identification document that clearly shows your face. Copies or scanned documents are not accepted."
          );
    }

    const breakdown = output?.document_report?.breakdown;

    if (!breakdown) return _t("onfidoFallbackMessage");

    const isSanctions =
      breakdown?.image_integrity?.breakdown?.supported_document?.properties
        ?.issuing_country_subject_to_sanctions;

    if (isSanctions) {
      return _t("onfidoImageIntegritySanctionsMessage");
    }

    for (const { path, message, explanation } of ONFIDO_TOOLTIP_PRIORITY) {
      if (getConsiderResult(breakdown, path)) {
        if (path?.includes("image_quality")) {
          return `${_t(message)}: ${_t(explanation)}`;
        }
        return _t(message);
      }
    }

    const hasAnyConsider = Object.values(breakdown).some(
      (section) =>
        section?.result === "consider" ||
        Object.values(section?.breakdown ?? {}).some(
          (field) =>
            field?.result === "consider" ||
            Object.values(field?.properties ?? {}).some(
              (value) => value === "consider"
            )
        )
    );

    return hasAnyConsider ? _t("onfidoFallbackMessage") : null;
  };

  const kycReviewReason = getReviewReason();
  const pendingActionLabel =
    pendingActionCount === 1 ? _t("pendingAction") : _t("pendingActions");

  const isGuarantorFor = isGuarantor || (user?.gurantor_for?.length ?? 0) > 0;
  const guarantorForCount = user?.gurantor_for?.length ?? 0;
  const canOpenGuarantorFor =
    isGuarantorFor &&
    guarantorForCount > 0 &&
    typeof onOpenGuarantor === "function";

  // Guarantor complete / pending breakdown for the header value.
  // Complete = accepted guarantor whose profile is finished (forensic ready).
  // Everything else (pending invite, processing, docs/KYC missing) = pending.
  const getGuarantorCounts = () => {
    if (guarantorCompleteCount !== null && guarantorPendingCount !== null) {
      return {
        complete: guarantorCompleteCount,
        pending: guarantorPendingCount,
      };
    }
    const entries = Array.isArray(user?.gurantor_added_id)
      ? user.gurantor_added_id
      : [];
    const flagged = Array.isArray(
      user?.tenant_extra_field_id?.selected_onboarding_users
    )
      ? user.tenant_extra_field_id.selected_onboarding_users.filter(
          (item) => item?.type === "guarantor"
        )
      : [];
    let complete = 0;
    let pending = flagged.length;
    entries.forEach((entry) => {
      const match = (gurantorsIncome ?? []).find(
        (g) => g?.all_sources?._id === entry?.guarantor_id?._id
      );
      let statusKey = null;
      if (match) {
        statusKey = getGuarantorStatus("accepted", match?.all_sources)?.status;
      } else {
        statusKey = getGuarantorStatus(
          entry?.status,
          entry?.guarantor_id
        )?.status;
      }
      if (statusKey === "completed") complete += 1;
      else pending += 1;
    });
    // Fallback to the numeric prop when user data is not yet available
    if (entries.length === 0 && flagged.length === 0 && guarantorCount > 0) {
      pending = guarantorCount;
    }
    return { complete, pending };
  };
  const { complete: guarantorComplete, pending: guarantorPending } =
    getGuarantorCounts();
  const totalGuarantors = guarantorComplete + guarantorPending;
  const guarantorValueLabel =
    totalGuarantors === 0
      ? _t("none", "None")
      : guarantorComplete > 0 && guarantorPending === 0
        ? `${guarantorComplete} ${_t("complete", "Complete")}`
        : guarantorComplete === 0 && guarantorPending > 0
          ? `${guarantorPending} ${_t("pending", "Pending")}`
          : `${guarantorComplete} ${_t("complete", "Complete")} · ${guarantorPending} ${_t("pending", "Pending")}`;
  const guarantorCtaLabel =
    totalGuarantors === 0 ? (
      <>+ {_t("add", "Add")}</>
    ) : (
      _t("addorview", "Add/View")
    );
  const applicationValueLabel =
    !flatmateCount || flatmateCount === 0
      ? _t("none", "None")
      : `${flatmateCount}`;
  const applicationCtaLabel =
    !flatmateCount || flatmateCount === 0 ? (
      <>+ {_t("new", "New")}</>
    ) : (
      _t("addorview", "Add/View")
    );

  // Bank behaviour error: classification wins over Algoan error when available;
  // unknown/missing classification falls back to the Algoan error.
  const bankDisplayError = resolveFinancialDisplayError({
    classification: bankStatementClassification,
    financialInfoErrors: financial_info_errors,
    userFinancialInfoErrors: user?.financial_info_errors,
  });
  const hasBankError = Boolean(bankDisplayError.key);
  const bankErrorTitle = bankDisplayError.titleKey
    ? _t(bankDisplayError.titleKey, bankDisplayError.titleFallback)
    : null;
  const bankErrorSubtitle = bankDisplayError.subtitleKey
    ? _t(bankDisplayError.subtitleKey, bankDisplayError.subtitleFallback)
    : null;
  const bankErrorText = hasBankError
    ? bankErrorSubtitle || _t(bankDisplayError.key, bankDisplayError.fallback)
    : null;

  return (
    <section className={styles.card} aria-label="tenant overview">
      <div className={styles.top}>
        <div className={styles.left}>
          <ProfileCompletionPercentage
            percent={percent}
            loading={percentageLoading}
          />
          <div className={styles.missing}>
            <div className={styles.missingText}>
              {missingDocs.length > 0
                ? _t(
                    "docsAwayFromComplete",
                    `${missingDocs.length} documents away from a complete profile`,
                    undefined,
                    { count: missingDocs.length }
                  )
                : null}
            </div>
            <div className={styles.chips}>
              {visibleDocs.map((d, i) => (
                <span key={i} className={styles.chip}>
                  <svg
                    width="12"
                    height="12"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    aria-hidden="true"
                  >
                    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                    <polyline points="14 2 14 8 20 8" />
                  </svg>
                  {_t(
                    typeof d === "string" ? d : d[0],
                    typeof d === "string" ? d : d[0],
                    "fixed"
                  )}
                </span>
              ))}
              {missingDocs.length > 3 && (
                <button
                  type="button"
                  className={styles.moreLink}
                  onClick={() => setExpanded((v) => !v)}
                >
                  {expanded
                    ? _t("less", "Less")
                    : `+${missingDocs.length - 3} ${_t("more", "more")}`}
                </button>
              )}
            </div>
          </div>
        </div>
        <aside
          className={`${styles.note} ${user?.skor_protect ? styles.noteWithBadge : ""}`}
          aria-labelledby="landlord-note-title"
        >
          <div className={styles.noteHeader}>
            {user?.skor_protect && (
              <span className={styles.skorProtectBadge}>
                <SkorProtectBadge />
              </span>
            )}
            <span id="landlord-note-title" className={styles.noteTitle}>
              {_t("aNoteToLandlords", "A note to landlords")}
            </span>
            {!readOnly &&
              (disabled ? (
                <div
                  style={{ display: "inline-flex", alignItems: "center" }}
                  data-tooltip-id="thc-edit-tooltip"
                >
                  <button
                    type="button"
                    className={styles.noteEditBtn}
                    onClick={onEditLandlordNote}
                    disabled={disabled}
                    style={disabledStyle}
                    aria-label={_t("edit", "Edit")}
                  >
                    <PencilEditIcon color="currentColor" size={16} />
                  </button>
                  {renderTooltip("thc-edit-tooltip")}
                </div>
              ) : (
                <button
                  type="button"
                  className={styles.noteEditBtn}
                  onClick={onEditLandlordNote}
                  aria-label={_t("edit", "Edit")}
                >
                  <PencilEditIcon color="currentColor" size={16} />
                </button>
              ))}
          </div>
        </aside>
      </div>

      <div className={styles.grid}>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("verification", "Verification")}
          </div>
          <div className={styles.boxValue}>
            {isKycFailed ? (
              <span
                data-tooltip-id="thc-kyc-error"
                className={styles.kycErrorTrigger}
              >
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  <img
                    src={
                      kycState === "review"
                        ? "/images/warning.svg"
                        : "/images/high-risk.svg"
                    }
                    alt=""
                    width="16"
                    height="16"
                  />
                </span>
                <span>{_t(kycState)}</span>
              </span>
            ) : (
              <span className={styles.kycErrorTrigger}>
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  {kycState == "approved" && (
                    <img
                      src="/images/trusted.svg"
                      alt=""
                      width="16"
                      height="16"
                    />
                  )}
                </span>
                <span>{_t(kycState)}</span>
              </span>
            )}{" "}
            {!readOnly &&
              (disabled ? (
                <div
                  style={{ display: "inline-block", marginLeft: "auto" }}
                  data-tooltip-id="thc-redo-tooltip"
                >
                  <button
                    type="button"
                    className={`${kycActionClass} ${styles.redoAction}`}
                    onClick={onRedoKyc}
                    disabled={disabled}
                    style={disabledStyle}
                  >
                    <img
                      src="/images/redoIcon.svg"
                      alt=""
                      className={styles.redoIcon}
                      aria-hidden="true"
                    />
                    {_t("redo", "Redo")}
                  </button>
                  {renderTooltip("thc-redo-tooltip")}
                </div>
              ) : (
                <button
                  type="button"
                  className={`${kycActionClass} ${styles.redoAction}`}
                  onClick={onRedoKyc}
                >
                  <img
                    src="/images/redoIcon.svg"
                    alt=""
                    className={styles.redoIcon}
                    aria-hidden="true"
                  />
                  {_t("redo", "Redo")}
                </button>
              ))}
            {isKycFailed && kycReviewReason ? (
              <ReactTooltip
                id="thc-kyc-error"
                place="bottom"
                className={styles.kycTooltip}
                render={() => (
                  <div>
                    <p className={styles.kycTooltipText}>{kycReviewReason}</p>
                  </div>
                )}
              />
            ) : null}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("bankBehaviour", "Bank behaviour")}
          </div>
          <div className={styles.boxValue}>
            {hasBankError ? (
              <span
                data-tooltip-id="thc-bank-error"
                className={styles.kycErrorTrigger}
              >
                <span className={styles.kycErrorIcon} aria-hidden="true">
                  <img src="/images/high-risk.svg" alt="" />
                </span>
                <span>{_t("error", "Error")}</span>
              </span>
            ) : (
              <span className={!bankConnected ? styles.dimmed : undefined}>
                {bankConnected
                  ? _t("banklinked", "Linked")
                  : _t("banknotLinked", "Not linked")}
              </span>
            )}{" "}
            {!readOnly &&
              (hasBankError ? (
                disabled ? (
                  <div
                    style={{ display: "inline-block", marginLeft: "auto" }}
                    data-tooltip-id="thc-bank-retry-tooltip"
                  >
                    <button
                      type="button"
                      className={`${styles.action} ${styles.redoAction}`}
                      onClick={onConnectBank}
                      disabled={disabled}
                      style={disabledStyle}
                    >
                      <img
                        src="/images/redoIcon.svg"
                        alt=""
                        className={styles.redoIcon}
                        aria-hidden="true"
                      />
                      {_t("redo", "Redo")}
                    </button>
                    {renderTooltip("thc-bank-retry-tooltip")}
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`${styles.action} ${styles.redoAction}`}
                    onClick={onConnectBank}
                  >
                    <img
                      src="/images/redoIcon.svg"
                      alt=""
                      className={styles.redoIcon}
                      aria-hidden="true"
                    />
                    {_t("redo", "Redo")}
                  </button>
                )
              ) : (
                !bankConnected &&
                (disabled ? (
                  <div
                    style={{ display: "inline-block", marginLeft: "auto" }}
                    data-tooltip-id="thc-connect-tooltip"
                  >
                    <button
                      type="button"
                      className={`${styles.action} ${styles.connectAction}`}
                      onClick={onConnectBank}
                      disabled={disabled}
                      style={disabledStyle}
                    >
                      <ChainLinkIcon
                        className={styles.connectIcon}
                        aria-hidden="true"
                      />
                      {_t("connect", "Connect")}
                    </button>
                    {renderTooltip("thc-connect-tooltip")}
                  </div>
                ) : (
                  <button
                    type="button"
                    className={`${styles.action} ${styles.connectAction}`}
                    onClick={onConnectBank}
                  >
                    <ChainLinkIcon
                      className={styles.connectIcon}
                      aria-hidden="true"
                    />
                    {_t("connect", "Connect")}
                  </button>
                ))
              ))}
            {hasBankError && bankErrorText ? (
              <ReactTooltip
                id="thc-bank-error"
                place="bottom"
                className={styles.kycTooltip}
                render={() => (
                  <div>
                    {bankErrorTitle ? (
                      <p className={styles.kycTooltipTitle}>{bankErrorTitle}</p>
                    ) : null}
                    <p className={styles.kycTooltipText}>{bankErrorText}</p>
                  </div>
                )}
              />
            ) : null}
          </div>
        </div>
        <div className={styles.box}>
          <div className={styles.boxLabel}>
            {_t("rentalBarometer", "Rental barometer")}
          </div>
          <div
            className={`${styles.boxValue} ${isRentalElevated ? styles.danger : ""}`}
          >
            {hasRentalStatus ? (
              _t(normalizedRentalStatus.toLowerCase(), normalizedRentalStatus)
            ) : (
              <>
                <span className={styles.danger}>{_t("notSet", "Not set")}</span>
                {!readOnly &&
                  (disabled ? (
                    <div
                      style={{ display: "inline-block", marginLeft: "auto" }}
                      data-tooltip-id="thc-target-rent-tooltip"
                    >
                      <button
                        type="button"
                        className={styles.action}
                        onClick={onSetTargetRent}
                        disabled={disabled}
                        style={disabledStyle}
                      >
                        {_t("set", "Set")}
                      </button>
                      {renderTooltip("thc-target-rent-tooltip")}
                    </div>
                  ) : (
                    <button
                      type="button"
                      className={styles.action}
                      onClick={onSetTargetRent}
                    >
                      {_t("set", "Set")}
                    </button>
                  ))}
              </>
            )}
          </div>
        </div>
        {isGuarantorFor ? (
          <div
            className={styles.box}
            onClick={canOpenGuarantorFor ? onOpenGuarantor : undefined}
            style={canOpenGuarantorFor ? { cursor: "pointer" } : undefined}
            role={canOpenGuarantorFor ? "button" : undefined}
            tabIndex={canOpenGuarantorFor ? 0 : undefined}
            onKeyDown={
              canOpenGuarantorFor
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onOpenGuarantor();
                    }
                  }
                : undefined
            }
            aria-label={_t("guarantorFor", "Guarantor for")}
          >
            <div className={styles.boxLabel}>
              {_t("guarantorFor", "Guarantor for")}
            </div>
            <div className={styles.boxValue}>
              {guarantorForCount}{" "}
              {canOpenGuarantorFor && (
                <button
                  type="button"
                  className={weakActionClass}
                  onClick={(e) => {
                    e.stopPropagation();
                    onOpenGuarantor();
                  }}
                >
                  {_t("viewDetails", "View details")}
                </button>
              )}
            </div>
          </div>
        ) : (
          <div
            className={styles.box}
            onClick={!readOnly && !disabled ? onAddGuarantor : undefined}
            style={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? { cursor: "pointer" }
                : undefined
            }
            role={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? "button"
                : undefined
            }
            tabIndex={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? 0
                : undefined
            }
            onKeyDown={
              !readOnly && !disabled && typeof onAddGuarantor === "function"
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onAddGuarantor();
                    }
                  }
                : undefined
            }
            aria-label={_t("guarantors", "Guarantors")}
            data-tooltip-id={disabled ? "thc-guarantor-tooltip" : undefined}
          >
            <div className={styles.boxLabel}>
              {_t("guarantors", "Guarantors")}
            </div>
            <div className={styles.boxValue}>
              {guarantorValueLabel}{" "}
              {!readOnly &&
                (disabled ? (
                  <div style={{ display: "inline-block", marginLeft: "auto" }}>
                    <button
                      type="button"
                      className={weakActionClass}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddGuarantor?.(e);
                      }}
                      disabled={disabled}
                      style={disabledStyle}
                    >
                      {guarantorCtaLabel}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={weakActionClass}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddGuarantor?.(e);
                    }}
                  >
                    {guarantorCtaLabel}
                  </button>
                ))}
            </div>
            {renderTooltip("thc-guarantor-tooltip")}
          </div>
        )}
        {!isGuarantorFor && (
          <div
            className={styles.box}
            onClick={!readOnly && !disabled ? onAddFlatmate : undefined}
            style={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? { cursor: "pointer" }
                : undefined
            }
            role={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? "button"
                : undefined
            }
            tabIndex={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? 0
                : undefined
            }
            onKeyDown={
              !readOnly && !disabled && typeof onAddFlatmate === "function"
                ? (e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onAddFlatmate();
                    }
                  }
                : undefined
            }
            aria-label={_t("applications", "Applications")}
            data-tooltip-id={disabled ? "thc-flatmate-tooltip" : undefined}
          >
            <div className={styles.boxHeader}>
              <div className={styles.boxLabel}>
                {_t("applications", "Applications")}
              </div>
              {pendingActionCount > 0 && (
                <button
                  type="button"
                  className={styles.pendingActionChip}
                  onClick={(e) => {
                    e.stopPropagation();
                    onPendingActionClick?.(e);
                  }}
                >
                  <span
                    className={styles.pendingActionDot}
                    aria-hidden="true"
                  />
                  {pendingActionCount} {pendingActionLabel}
                </button>
              )}
            </div>
            <div className={styles.boxValue}>
              {applicationValueLabel}{" "}
              {!readOnly &&
                (disabled ? (
                  <div style={{ display: "inline-block", marginLeft: "auto" }}>
                    <button
                      type="button"
                      className={weakActionClass}
                      onClick={(e) => {
                        e.stopPropagation();
                        onAddFlatmate?.(e);
                      }}
                      disabled={disabled}
                      style={disabledStyle}
                    >
                      {applicationCtaLabel}
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    className={weakActionClass}
                    onClick={(e) => {
                      e.stopPropagation();
                      onAddFlatmate?.(e);
                    }}
                  >
                    {applicationCtaLabel}
                  </button>
                ))}
            </div>
            {renderTooltip("thc-flatmate-tooltip")}
          </div>
        )}
      </div>
    </section>
  );
};

export default TenantHeaderCard;
